package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SupplierItemsScreen(
    supplierId: String,
    supplierName: String,
    onBack: () -> Unit
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var items by remember { mutableStateOf<List<Item>>(emptyList()) }
    var searchQuery by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    fun refreshItems(silent: Boolean = false) {
        scope.launch {
            if (!silent) isLoading = true
            repository.getItems(supplierId).fold(
                onSuccess = { 
                    items = it
                    isLoading = false
                },
                onFailure = { 
                    error = it.message
                    isLoading = false
                }
            )
        }
    }

    LaunchedEffect(supplierId) {
        refreshItems()
    }

    var showAddDialog by remember { mutableStateOf(false) }
    var showEditDialog by remember { mutableStateOf<Item?>(null) }
    var itemToArchive by remember { mutableStateOf<Item?>(null) }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                TopAppBar(
                    title = { 
                        Column {
                            Text(supplierName, style = MaterialTheme.typography.titleMedium)
                            Text("Items", style = MaterialTheme.typography.bodySmall)
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        if (userRole == UserRole.ADMIN || userRole == UserRole.EDITOR) {
                            Button(
                                onClick = { showAddDialog = true },
                                shape = MaterialTheme.shapes.medium,
                                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary),
                                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 0.dp),
                                modifier = Modifier.padding(end = 8.dp).height(36.dp)
                            ) {
                                Text("+ Add", style = MaterialTheme.typography.labelLarge)
                            }
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Column(modifier = Modifier.align(Alignment.Center), horizontalAlignment = Alignment.CenterHorizontally) {
                        Text("Error: $error", color = MaterialTheme.colorScheme.error)
                        Button(onClick = { refreshItems() }) { Text("Retry") }
                    }
                } else if (!isLoading) {
                    val filteredItems = items.filter { it.name.contains(searchQuery, ignoreCase = true) }
                    
                    Column(modifier = Modifier.fillMaxSize()) {
                        OutlinedTextField(
                            value = searchQuery,
                            onValueChange = { searchQuery = it },
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(16.dp),
                            placeholder = { Text("Search items...") },
                            trailingIcon = {
                                if (searchQuery.isNotEmpty()) {
                                    IconButton(onClick = { searchQuery = "" }) {
                                        CloseIcon()
                                    }
                                }
                            },
                            singleLine = true,
                            shape = MaterialTheme.shapes.medium,
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedContainerColor = MaterialTheme.colorScheme.surface,
                                unfocusedContainerColor = MaterialTheme.colorScheme.surface
                            )
                        )
                        
                        if (filteredItems.isEmpty()) {
                            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                Text("No items match your search.")
                            }
                        } else {
                            LazyColumn(modifier = Modifier.fillMaxSize()) {
                                items(filteredItems, key = { it.id }) { item ->
                                    ItemRow(
                                        item = item, 
                                        onClick = { 
                                            if (userRole == UserRole.ADMIN || userRole == UserRole.EDITOR) {
                                                showEditDialog = item 
                                            }
                                        }
                                    )
                                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                                }
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }

    if (showAddDialog) {
        ItemDialog(
            title = "Add New Item",
            onDismiss = { showAddDialog = false },
            onConfirm = { name, desc, unit, pBox, mPack, usage, price ->
                scope.launch {
                    repository.createItem(
                        CreateItemRequest(
                            supplierId = supplierId,
                            name = name,
                            packDescription = desc,
                            unitMeasureType = unit,
                            packsPerBox = pBox,
                            measurePerPack = mPack,
                            weeklyUsageRate = usage,
                            price = price
                        )
                    ).fold(
                        onSuccess = { response ->
                            if (response.error == null) {
                                showAddDialog = false
                                refreshItems(silent = true)
                                snackbarHostState.showSnackbar(response.message ?: "Item created successfully")
                            } else {
                                snackbarHostState.showSnackbar("Error: ${response.error}")
                            }
                        },
                        onFailure = { 
                            snackbarHostState.showSnackbar("Failed to connect to server: ${it.message}")
                        }
                    )
                }
            }
        )
    }

    showEditDialog?.let { item ->
        ItemDialog(
            title = "Edit Item",
            initialItem = item,
            onDismiss = { showEditDialog = null },
            onArchive = if (userRole == UserRole.ADMIN || userRole == UserRole.EDITOR) {
                { itemToArchive = item }
            } else null,
            onConfirm = { name, desc, unit, pBox, mPack, usage, price ->
                scope.launch {
                    repository.editItem(
                        EditItemRequest(
                            id = item.id,
                            name = name,
                            packDescription = desc,
                            unitMeasureType = unit,
                            packsPerBox = pBox,
                            measurePerPack = mPack,
                            weeklyUsageRate = usage,
                            price = price
                        )
                    ).fold(
                        onSuccess = { response ->
                            if (response.error == null) {
                                showEditDialog = null
                                refreshItems(silent = true)
                                snackbarHostState.showSnackbar(response.message ?: "Item updated successfully")
                            } else {
                                snackbarHostState.showSnackbar("Error: ${response.error}")
                            }
                        },
                        onFailure = { 
                            snackbarHostState.showSnackbar("Failed to connect to server: ${it.message}")
                        }
                    )
                }
            }
        )
    }

    itemToArchive?.let { item ->
        AlertDialog(
            onDismissRequest = { itemToArchive = null },
            title = { Text("Archive Item") },
            text = { Text("Are you sure you want to archive '${item.name}'? It will be removed from this supplier's list.") },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            repository.editItem(
                                EditItemRequest(
                                    id = item.id,
                                    supplierId = "99",
                                    name = item.name,
                                    packDescription = item.packDescription,
                                    unitMeasureType = item.unitMeasureType,
                                    packsPerBox = item.packsPerBox,
                                    measurePerPack = item.measurePerPack,
                                    weeklyUsageRate = item.weeklyUsageRate,
                                    price = item.price
                                )
                            ).fold(
                                onSuccess = { response ->
                                    if (response.error == null) {
                                        showEditDialog = null
                                        itemToArchive = null
                                        refreshItems(silent = true)
                                        snackbarHostState.showSnackbar(response.message ?: "Item archived successfully")
                                    } else {
                                        snackbarHostState.showSnackbar("Error: ${response.error}")
                                    }
                                },
                                onFailure = {
                                    snackbarHostState.showSnackbar("Failed to connect to server: ${it.message}")
                                }
                            )
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Archive", color = MaterialTheme.colorScheme.onError)
                }
            },
            dismissButton = {
                TextButton(onClick = { itemToArchive = null }) {
                    Text("Cancel")
                }
            },
            shape = MaterialTheme.shapes.large
        )
    }
}

@Composable
fun ItemRow(item: Item, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = item.name,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f),
                maxLines = 2
            )
            val userRole = LocalUserRole.current
            if (userRole == UserRole.ADMIN) {
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "£${item.price}",
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.secondary,
                    fontWeight = FontWeight.Bold
                )
            }
        }
        Text("${item.packDescription} (${item.unitMeasureType})", style = MaterialTheme.typography.bodyMedium)
        Row(modifier = Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            Text("Packs/Box: ${formatWhole(item.packsPerBox)}", style = MaterialTheme.typography.bodySmall)
            Text("Measure/Pack: ${formatWhole(item.measurePerPack)}", style = MaterialTheme.typography.bodySmall)
            Text("Weekly Usage: ${formatWhole(item.weeklyUsageRate)}", style = MaterialTheme.typography.bodySmall)
        }
    }
}

@Composable
fun ItemDialog(
    title: String,
    initialItem: Item? = null,
    onDismiss: () -> Unit,
    onArchive: (() -> Unit)? = null,
    onConfirm: (String, String, String, Double, Double, Double, Double) -> Unit
) {
    var name by remember { mutableStateOf(initialItem?.name ?: "") }
    var desc by remember { mutableStateOf(initialItem?.packDescription ?: "") }
    var unit by remember { mutableStateOf(initialItem?.unitMeasureType ?: "") }
    var pBox by remember { mutableStateOf(initialItem?.packsPerBox?.let { formatWhole(it) } ?: "") }
    var mPack by remember { mutableStateOf(initialItem?.measurePerPack?.let { formatWhole(it) } ?: "") }
    var usage by remember { mutableStateOf(initialItem?.weeklyUsageRate?.let { formatWhole(it) } ?: "") }
    var price by remember { mutableStateOf(initialItem?.price?.let { formatDecimal(it) } ?: "") }
    
    val userRole = LocalUserRole.current

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(modifier = Modifier.verticalScroll(androidx.compose.foundation.rememberScrollState()).fillMaxWidth()) {
                OutlinedTextField(value = name, onValueChange = { name = it }, label = { Text("Item Name") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                OutlinedTextField(value = desc, onValueChange = { desc = it }, label = { Text("Pack Description") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                OutlinedTextField(value = unit, onValueChange = { unit = it }, label = { Text("Unit Measure Type (e.g. kg, L)") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                OutlinedTextField(value = pBox, onValueChange = { pBox = it }, label = { Text("Packs per Box") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                OutlinedTextField(value = mPack, onValueChange = { mPack = it }, label = { Text("Measure per Pack") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                OutlinedTextField(value = usage, onValueChange = { usage = it }, label = { Text("Weekly Usage Rate") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                
                if (userRole == UserRole.ADMIN) {
                    OutlinedTextField(value = price, onValueChange = { price = it }, label = { Text("Price (£)") }, modifier = Modifier.fillMaxWidth(), shape = MaterialTheme.shapes.small)
                }

                if (onArchive != null) {
                    Spacer(modifier = Modifier.height(16.dp))
                    OutlinedButton(
                        onClick = onArchive,
                        modifier = Modifier.fillMaxWidth(),
                        colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error)
                    ) {
                        Text("Archive Item", color = MaterialTheme.colorScheme.error)
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { 
                    onConfirm(
                        name, desc, unit, 
                        pBox.toDoubleOrNull() ?: 0.0, 
                        mPack.toDoubleOrNull() ?: 0.0, 
                        usage.toDoubleOrNull() ?: 0.0, 
                        if (userRole == UserRole.ADMIN) (price.toDoubleOrNull() ?: 0.0) else (initialItem?.price ?: 0.0)
                    ) 
                }, 
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        },
        shape = MaterialTheme.shapes.large
    )
}
